export interface SpotRoundSequenceItem {
  id: number | string;
  name?: string;
  previousRoundId?: number | null;
}

/** Extract display sequence from names like "Round 1", "Round #2". */
export function extractRoundSequenceNumber(value?: string | null): number | null {
  if (!value?.trim()) {
    return null;
  }

  const trimmed = value.trim();
  const roundMatch = trimmed.match(/round\s*#?\s*(\d+)/i);
  if (roundMatch) {
    const num = Number(roundMatch[1]);
    return Number.isFinite(num) ? num : null;
  }

  const trailingMatch = trimmed.match(/(\d+)\s*$/);
  if (trailingMatch) {
    const num = Number(trailingMatch[1]);
    return Number.isFinite(num) ? num : null;
  }

  return null;
}

function compareRoundIds(a: string | number, b: string | number): number {
  const left = Number(a);
  const right = Number(b);
  if (Number.isFinite(left) && Number.isFinite(right)) {
    return left - right;
  }
  return String(a).localeCompare(String(b));
}

function compareRoundsBySequence(a: SpotRoundSequenceItem, b: SpotRoundSequenceItem): number {
  const seqA = extractRoundSequenceNumber(a.name);
  const seqB = extractRoundSequenceNumber(b.name);
  if (seqA !== null && seqB !== null && seqA !== seqB) {
    return seqA - seqB;
  }
  if (seqA !== null && seqB === null) {
    return -1;
  }
  if (seqA === null && seqB !== null) {
    return 1;
  }
  return compareRoundIds(a.id, b.id);
}

/**
 * Sort SpotRound rows ascending: previousRoundId chain, then name number, then id.
 * Logs original and sorted lists when logLabel is provided.
 */
export function sortSpotRoundsBySequenceAsc<T extends SpotRoundSequenceItem>(
  apiRounds: T[],
  logLabel = "[RoundOrder]",
): T[] {
  console.log(
    `${logLabel} original rounds response`,
    apiRounds.map((round) => ({
      id: round.id,
      name: round.name,
      previousRoundId: round.previousRoundId ?? null,
    })),
  );

  if (apiRounds.length <= 1) {
    return [...apiRounds];
  }

  const byId = new Map(apiRounds.map((round) => [Number(round.id), round]));
  const head = apiRounds.find(
    (round) => round.previousRoundId == null || !byId.has(Number(round.previousRoundId)),
  );

  if (head) {
    const ordered: T[] = [];
    const seen = new Set<number>();
    let current: T | undefined = head;
    while (current && !seen.has(Number(current.id))) {
      seen.add(Number(current.id));
      ordered.push(current);
      const currentId: number = Number(current.id);
      current = apiRounds.find(
        (round) => Number(round.previousRoundId) === currentId,
      );
    }
    if (ordered.length === apiRounds.length) {
      console.log(
        `${logLabel} sorted rounds list`,
        ordered.map((round) => ({ id: round.id, name: round.name })),
      );
      return ordered;
    }
  }

  const sorted = [...apiRounds].sort(compareRoundsBySequence);
  console.log(
    `${logLabel} sorted rounds list`,
    sorted.map((round) => ({ id: round.id, name: round.name })),
  );
  return sorted;
}

/** Sort mapped sidebar rounds ascending by display sequence. */
export function sortMappedSpotRoundsAsc<T extends { id: string; name: string }>(
  rounds: T[],
  logLabel = "[RoundOrder]",
): T[] {
  const sorted = [...rounds].sort((a, b) =>
    compareRoundsBySequence(
      { id: a.id, name: a.name },
      { id: b.id, name: b.name },
    ),
  );
  console.log(
    `${logLabel} sorted spot rounds list`,
    sorted.map((round) => ({ id: round.id, name: round.name })),
  );
  return sorted;
}
